import React, { useState, useEffect, useRef } from 'react';
import { generateDataset } from '../../utils/fine-tuning-functions';
import { queryAllDoctors } from '../../utils/functions';
import { format } from 'date-fns';

export default function GenerateDataset() {
  const [formData, setFormData] = useState({
    datasetName: '',
    doctors: [],
    startDate: '',
    endDate: format(new Date(), 'yyyy-MM-dd'),
    instruction: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState({ type: null, message: null });
  const [doctorsList, setDoctorsList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch doctors list when component mounts
  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        setIsLoading(true);
        const response = await queryAllDoctors();
        const doctors = response.map(doctor => ({
          id: doctor.user_id,
          name: doctor.user_name
        }));
        setDoctorsList(doctors);
      } catch (error) {
        console.error("Error fetching doctors:", error);
        setFeedback({
          type: "error",
          message: "Failed to load doctors list. Please try again."
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDoctors();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredDoctors = React.useMemo(() => {
    const sortedDoctors = [...doctorsList].sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    if (!searchTerm.trim()) {
      return sortedDoctors;
    }

    const lowerCaseSearch = searchTerm.toLowerCase();
    return sortedDoctors.filter(doctor =>
      doctor.name.toLowerCase().includes(lowerCaseSearch)
    );
  }, [doctorsList, searchTerm]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);

    setIsDropdownOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedFormData = {
      ...formData,
      [name]: value
    };

    setFormData(updatedFormData);

    // Clear feedback when user makes changes
    if (feedback.message) {
      setFeedback({ type: null, message: null });
    }

    // Validate dates immediately if both are set
    if ((name === 'startDate' || name === 'endDate') &&
      updatedFormData.startDate &&
      updatedFormData.endDate) {
      const startDate = new Date(updatedFormData.startDate);
      const endDate = new Date(updatedFormData.endDate);

      if (startDate > endDate) {
        setFeedback({
          type: "error",
          message: "Start date must be on or before end date"
        });
      }
    }
  };

  const addDoctor = (doctor) => {
    if (!formData.doctors.some(d => d.id === doctor.id)) {
      setFormData({
        ...formData,
        doctors: [...formData.doctors, doctor]
      });
    }

    setSearchTerm('');
    setIsDropdownOpen(false);
  };

  const removeDoctor = (doctorId) => {
    setFormData({
      ...formData,
      doctors: formData.doctors.filter(doctor => doctor.id !== doctorId)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Form validation
    if (!formData.datasetName.trim()) {
      setFeedback({
        type: "error",
        message: "Please provide a name for your dataset"
      });
      return;
    }

    if (formData.doctors.length === 0) {
      setFeedback({
        type: "error",
        message: "Please select at least one doctor"
      });
      return;
    }

    if (!formData.startDate) {
      setFeedback({
        type: "error",
        message: "Please select a start date"
      });
      return;
    }

    if (!formData.endDate) {
      setFeedback({
        type: "error",
        message: "Please select an end date"
      });
      return;
    }

    // Optional validation: Check if end date is after start date
    if (new Date(formData.endDate) < new Date(formData.startDate)) {
      setFeedback({
        type: "error",
        message: "End date must be after start date"
      });
      return;
    }

    setIsLoading(true);
    setFeedback({ type: null, message: null });

    try {
      // Format the data for the API
      const apiPayload = {
        datasetName: formData.datasetName,
        doctors: formData.doctors.map(doctor => doctor.name), // Send just the names
        startDate: formData.startDate,
        endDate: formData.endDate,
        instruction: formData.instruction
      };

      await generateDataset(apiPayload);

      setFeedback({
        type: "success",
        message: (
          <>
            Successfully generated dataset on huggingface: <span className="font-medium">Knowtex-ai/{formData.datasetName}</span>
            <div className="mt-2 text-sm text-blue-600">
              {/* <span className="inline-flex items-center">
                <svg className="mr-1.5 h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Please wait a few minutes for the dataset to be populated before importing into the fine-tuning database.
              </span> */}
            </div>
          </>
        )
      });

      // Reset form after successful generation
      setFormData({
        datasetName: '',
        doctors: [],
        startDate: '',
        endDate: format(new Date(), 'yyyy-MM-dd'),
        instruction: ''
      });
    } catch (error) {
      console.error("Error generating dataset:", error);
      setFeedback({
        type: "error",
        message: error.message || "Failed to generate dataset. Please try again."
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Generate Training Dataset</h3>
        <p className="text-gray-600">
          Generate a dataset based on doctor information and date range.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="datasetName" className="block text-sm font-medium text-gray-700 mb-1">
            Dataset Name
          </label>
          <div className="mt-1">
            <input
              type="text"
              id="datasetName"
              name="datasetName"
              value={formData.datasetName}
              onChange={handleInputChange}
              className="block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3 focus:ring-ktx-purple focus:border-ktx-purple"
              placeholder="e.g., cardiology-training-data"
              disabled={isLoading}
            />
          </div>
        </div>

        {/* Enhanced Doctor Selection with Dropdown and Chips */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Select Doctors
          </label>
          <div className="relative" ref={dropdownRef}>
            <div className="flex items-center">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={() => setIsDropdownOpen(true)}
                  placeholder="Search for doctors..."
                  className="block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3 focus:ring-ktx-purple focus:border-ktx-purple"
                  disabled={isLoading}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Dropdown menu with error handling */}
            {isDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm">
                {filteredDoctors && filteredDoctors.length > 0 ? (
                  filteredDoctors.map((doctor) => (
                    <div
                      key={doctor.id}
                      onClick={() => addDoctor(doctor)}
                      className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
                    >
                      <span className="block truncate font-medium">{doctor.name}</span>
                    </div>
                  ))
                ) : (
                  <div className="py-2 px-3 text-gray-500">
                    {searchTerm.trim() ? "No doctors found" : "Loading doctors..."}
                  </div>
                )}
              </div>
            )}

            {/* Selected doctors chips */}
            {formData.doctors.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.doctors.map((doctor) => (
                  <div
                    key={doctor.id}
                    className="bg-ktx-purple bg-opacity-10 text-ktx-purple rounded-full py-1 px-3 text-sm flex items-center"
                  >
                    <span className="mr-1">{doctor.name}</span>
                    <button
                      type="button"
                      onClick={() => removeDoctor(doctor.id)}
                      className="text-ktx-purple hover:text-ktx-purple-dark focus:outline-none"
                      disabled={isLoading}
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {formData.doctors.length === 0 && (
              <p className="mt-1 text-xs text-gray-500">
                Please select at least one doctor
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <div className="mt-1">
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                className="block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3 focus:ring-ktx-purple focus:border-ktx-purple"
                disabled={isLoading}
              />
            </div>
          </div>

          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <div className="mt-1">
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                className="block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3 focus:ring-ktx-purple focus:border-ktx-purple"
                disabled={isLoading}
              />
            </div>
          </div>
        </div>

        <div>
          <label htmlFor="instruction" className="block text-sm font-medium text-gray-700 mb-1">
            Instructions
          </label>
          <div className="mt-1">
            <textarea
              id="instruction"
              name="instruction"
              rows={4}
              value={formData.instruction}
              onChange={handleInputChange}
              placeholder="Provide any specific instructions for dataset generation..."
              className="block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3 focus:ring-ktx-purple focus:border-ktx-purple"
              disabled={isLoading}
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Detailed instructions help create more relevant examples.
          </p>
        </div>

        {feedback.message && (
          <div className={`p-3 rounded-md ${feedback.type === 'error'
            ? 'bg-red-50 text-red-700 border border-red-200'
            : 'bg-green-50 text-green-700 border border-green-200'
            }`}>
            <div className="flex">
              <div className="flex-shrink-0">
                {feedback.type === 'error' ? (
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                {typeof feedback.message === 'string' ? (
                  <p className="text-sm">{feedback.message}</p>
                ) : (
                  feedback.message
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isLoading}
            className={`bg-ktx-purple hover:bg-ktx-purple-dark text-white font-medium py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:ring-offset-2 transition-colors
            ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {isLoading ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Generating...
              </div>
            ) : (
              'Generate Dataset'
            )}
          </button>
        </div>
      </form>

      <div className="mt-8 p-4 bg-blue-50 rounded-md border border-blue-100">
        <h4 className="text-sm font-medium text-blue-800 mb-2">Dataset Generation Tips</h4>
        <ul className="space-y-1 text-sm text-blue-700">
          <li className="flex items-start">
            <svg className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span>Select a date range with sufficient patient encounters</span>
          </li>
          <li className="flex items-start">
            <svg className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span>Include detailed instructions about the desired format and content</span>
          </li>
          <li className="flex items-start">
            <svg className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span>Always review generated data before using for fine-tuning</span>
          </li>
        </ul>
      </div>
    </div>
  );
}